import {
  CareerHubModuleReactContext,
  LoadingStates,
  SectionLoader,
  TBDDescriptionMessage,
  careerStateSelector,
} from '@careerhub/common';
import { AccordionPanel, Body1Strong, Caption1, Skeleton, SkeletonItem } from '@fluentui/react-components';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorComponent } from '../Shared/ErrorComponent';
import { scrollToTop } from '../Shared/Shared';
import { EventName, FeatureName, SubFeatureName } from '../resources/TelemetryEventDetails';
import { useLevelOneAccordionPanelStyles } from './LevelOneAccordionPanel.styles';
import { ILevelOneProps } from './LevelOne.types';
import { useSelector } from 'react-redux';

interface RouteParams {
  name: string;
  id: string;
}

export const ProfessionAccordionPanel: React.FC = (props: ILevelOneProps) => {
  const routeParams = useParams<RouteParams>();
  const styles = useLevelOneAccordionPanelStyles();
  const { taxonomyTypes } = useSelector(careerStateSelector);

  //const [taxonomyTypes, setTaxonomyTypes] = React.useState<JobTaxonomyType[]>([]);
  const [disciplineHierarchyDataSource, setDisciplineHierarchyDataSource] = React.useState<ILevelOneProps[]>();
  const [apiCallLoadingState, setApiCallLoadingState] = React.useState(LoadingStates.NOT_STARTED);
  const { id } = props;
  const { telemetryClient, roleLibraryService } = React.useContext(CareerHubModuleReactContext);

  const fetchLevel2Data = React.useCallback(() => {
    setApiCallLoadingState(LoadingStates.STARTED);
    const level2Type = taxonomyTypes.find((item) => item.level === 2)?.id || 'Discipline';

    id &&
      roleLibraryService
        .getTaxonomicRank(level2Type, id)
        .then(async (data) => {
          let level2Data: ILevelOneProps[] = [];
          for (var i in data) {
            level2Data.push({
              id: data[i].id,
              name: data[i].name,
              children: data[i].childTaxonomyCount.toString(),
              isProfessionPage: true,
              route: `/${level2Type}/${routeParams.name}`,
            });
            if (data[i].description === TBDDescriptionMessage) {
              telemetryClient.logEvent(FeatureName.BrowseProfession, {
                EventName: EventName.UserSawTBD,
                discplineId: data[i].id,
              });
            }
          }
          setApiCallLoadingState(LoadingStates.SUCCEEDED);
          setDisciplineHierarchyDataSource(level2Data);
        })
        .catch((_error: any) => {
          setApiCallLoadingState(LoadingStates.FAILED);
        });
  }, [roleLibraryService, taxonomyTypes, id, routeParams.name, telemetryClient]);

  React.useEffect(() => {
    if (apiCallLoadingState == LoadingStates.NOT_STARTED) fetchLevel2Data();
  }, [apiCallLoadingState, fetchLevel2Data]);

  const getPlaceholderLoadComponent = () => {
    return (
      <Skeleton animation="pulse" style={{ columnCount: 2 }} className={styles.skeletonContainer}>
        {new Array(6).fill(0).map((_item, index) => {
          let gridColumn = (index % 2) + 1;
          return <SkeletonItem size={16} style={{ width: '65%', gridColumn: gridColumn, marginBottom: '12px' }} />;
        })}
      </Skeleton>
    );
  };

  const handleOnClick = (id?: string) => {
    scrollToTop();
    telemetryClient.logButtonClick(FeatureName.BrowseProfession, SubFeatureName.OpenDiscipline, {
      eventName: EventName.NavigateToDisciplinePage,
      disciplineId: id,
    });
  };
  const level2Type = taxonomyTypes.find((item) => item.level === 2)?.id || 'Discipline';

  return (
    <AccordionPanel className={styles.container}>
      <SectionLoader
        announce
        state={apiCallLoadingState}
        shimmerComponent={getPlaceholderLoadComponent()}
        errorComponent={<ErrorComponent description="No content" />}
      >
        <ul className={styles.dropdownList} aria-label={`${props.name} disciplines`}>
          {disciplineHierarchyDataSource?.map((disciplineHierarchyDataSource, index) => {
            let gridColumn = (index % 2) + 1;
            return (
              <li
                key={disciplineHierarchyDataSource.id}
                className={styles.dropDownItem}
                style={{ gridColumn: gridColumn }}
              >
                {disciplineHierarchyDataSource?.children !== '0' ? (
                  <Link
                    className={styles.link}
                    to={`${level2Type}/${disciplineHierarchyDataSource.id}`}
                    onClick={() => handleOnClick(disciplineHierarchyDataSource.id)}
                  >
                    <Body1Strong className={styles.dropdownFont}>{disciplineHierarchyDataSource.name}</Body1Strong>
                  </Link>
                ) : (
                  <div className={styles.textContents}>
                    <Body1Strong>{disciplineHierarchyDataSource.name}</Body1Strong>
                    <Caption1>{'No role data'}</Caption1>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </SectionLoader>
    </AccordionPanel>
  );
};
